import { useEffect, useState } from 'react'
import { fetchStill, releaseStill, type LiveStill } from './api'

// Slowly: gateways sit on ~100 ms tunnels, and some cameras slow down under
// frequent snapshots.
export const STILL_REFRESH_MS = 60_000

/**
 * A camera's live still, for a tile with no detection to show: fetched while
 * `el` is on screen and `wanted`, and again once a minute. A refresh that fails
 * keeps the last frame, whose age then says how old it is; null until one
 * arrives.
 */
export function useLiveStill(el: Element | null, siteId: string, camera: string, wanted: boolean): LiveStill | null {
  const [still, setStill] = useState<LiveStill | null>(null)
  const [onScreen, setOnScreen] = useState(false)

  useEffect(() => {
    if (!el || !wanted || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver((entries) => setOnScreen(entries[entries.length - 1].isIntersecting))
    observer.observe(el)
    return () => observer.disconnect()
  }, [el, wanted])
  const visible = onScreen || typeof IntersectionObserver === 'undefined'

  useEffect(() => {
    if (!wanted || !visible) return
    let cancelled = false
    const load = () => {
      if (document.visibilityState === 'hidden') return
      fetchStill(siteId, camera)
        .then((next) => {
          if (cancelled) releaseStill(next)
          else setStill(next)
        })
        .catch(() => {})
    }
    load()
    const timer = setInterval(load, STILL_REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [siteId, camera, wanted, visible])

  useEffect(() => () => { if (still) releaseStill(still) }, [still])

  return still
}
